let windows1251Decoder: TextDecoder | null = null;
let utf8Decoder: TextDecoder | null = null;
let windows1251EncoderMap: Map<string, number> | null = null;

function getWindows1251Decoder() {
  windows1251Decoder ??= new TextDecoder("windows-1251");
  return windows1251Decoder;
}

function getUtf8Decoder() {
  utf8Decoder ??= new TextDecoder("utf-8");
  return utf8Decoder;
}

function decodeWindows1251(bytes: number[]) {
  return getWindows1251Decoder().decode(Uint8Array.from(bytes));
}

function getWindows1251EncoderMap() {
  if (windows1251EncoderMap) {
    return windows1251EncoderMap;
  }

  const bytes = Array.from({ length: 256 }, (_, index) => index);
  const decoded = decodeWindows1251(bytes);
  windows1251EncoderMap = new Map();

  for (let index = 0; index < decoded.length; index += 1) {
    windows1251EncoderMap.set(decoded[index], index);
  }

  return windows1251EncoderMap;
}

function encodeWindows1251(value: string) {
  const map = getWindows1251EncoderMap();
  const bytes: number[] = [];

  for (const char of value) {
    const code = char.charCodeAt(0);
    bytes.push(code <= 0x7f ? code : map.get(char) ?? 0x3f);
  }

  return bytes;
}

function hasCyrillic(value: string) {
  return /[\u0410-\u044f\u0401\u0451]/u.test(value);
}

function normalizeTechnicalArtifacts(value: string) {
  return value
    .replace(/\u0000/g, "")
    .replace(/\r\n/g, "\n")
    .replace(/_x000D_/gi, " ")
    .replace(/&#x0*D;/gi, " ");
}

export function looksLikeBrokenCyrillic(value: string) {
  const current = value.trim();

  if (!current) {
    return false;
  }

  const suspiciousPairs =
    current.match(
      /(?:\u0420[\u0400-\u04FF]|\u0421[\u0400-\u04FF]|\u00D0[A-Za-z]|\u00D1[A-Za-z]|\u0432\u20AC|\u0432\u201A)/gu
    ) ?? [];
  const cyrillicChars = current.match(/[\u0410-\u044f\u0401\u0451]/gu) ?? [];
  const repeatedRs = current.match(/[\u0420\u0421]/gu) ?? [];

  if (suspiciousPairs.length < 3) {
    return false;
  }

  if (!cyrillicChars.length) {
    return true;
  }

  return repeatedRs.length >= Math.ceil(cyrillicChars.length * 0.32);
}

export function looksLikeShiftedCp1251(value: string) {
  if (!value || hasCyrillic(value)) {
    return false;
  }

  const stripped = value.replace(/<[^>]*>/g, "");
  const visible = stripped.replace(/\s+/g, "");

  if (visible.length < 8) {
    return false;
  }

  const suspiciousMatches = stripped.match(/[\u0010-\u004F]/g) ?? [];
  const controlMatches =
    stripped.match(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g) ?? [];
  const suspiciousRuns = stripped.match(/[\u0010-\u004F]{4,}/g) ?? [];

  if (controlMatches.length >= 1 && suspiciousMatches.length >= 6) {
    return true;
  }

  if (suspiciousRuns.length >= 2) {
    return true;
  }

  return suspiciousMatches.length >= Math.max(8, Math.ceil(visible.length * 0.28));
}

export function looksLikeBrokenStoredText(value: string) {
  const current = normalizeTechnicalArtifacts(value.trim());

  if (!current) {
    return false;
  }

  if (looksLikeBrokenCyrillic(current) || looksLikeShiftedCp1251(current)) {
    return true;
  }

  if (/[\u0000-\u001F]/.test(current)) {
    return true;
  }

  const questionMarks = (current.match(/\?/g) ?? []).length;
  if (questionMarks >= Math.max(3, Math.ceil(current.length / 6))) {
    return true;
  }

  const suspiciousAsciiCount = (current.match(/[0-9;:<>=@]/g) ?? []).length;

  if (!hasCyrillic(current) && suspiciousAsciiCount >= Math.max(4, Math.ceil(current.length / 10))) {
    return true;
  }

  return !hasCyrillic(current) && /[A-Za-z]/.test(current) && /[;:<>=@]/.test(current);
}

function tryRepairUtf8Mojibake(value: string) {
  try {
    return getUtf8Decoder().decode(Uint8Array.from(encodeWindows1251(value)));
  } catch {
    return value;
  }
}

function scoreReadableCyrillic(value: string) {
  const cyrillic = value.match(/[\u0410-\u044f\u0401\u0451]/gu)?.length ?? 0;
  const broken =
    value.match(/(?:\u0420[\u0400-\u04FF]|\u0421[\u0400-\u04FF]|\u00D0[A-Za-z]|\u00D1[A-Za-z])/gu)
      ?.length ?? 0;
  const controls = value.match(/[\u0000-\u001F]/g)?.length ?? 0;
  return cyrillic * 2 - broken * 3 - controls * 4;
}

function tryRepairShiftedCp1251(value: string) {
  try {
    return value.replace(/[\u0010-\u004F]{3,}/g, (run) => {
      if (!/[\u0010-\u001F;<>=@A-Za-z]/.test(run)) {
        return run;
      }

      const bytes = Array.from(run, (char) => {
        const code = char.charCodeAt(0);
        if (code >= 0x10 && code <= 0x4f) {
          return code + 0xb0;
        }
        return code;
      });

      return decodeWindows1251(bytes);
    });
  } catch {
    return value;
  }
}

export function fixBrokenCyrillic(value: string) {
  let next = normalizeTechnicalArtifacts(value);

  for (let index = 0; index < 4; index += 1) {
    let changed = false;

    if (looksLikeShiftedCp1251(next)) {
      const decoded = normalizeTechnicalArtifacts(tryRepairShiftedCp1251(next));
      if (decoded && decoded !== next) {
        next = decoded;
        changed = true;
      }
    }

    if (looksLikeBrokenCyrillic(next)) {
      const decoded = normalizeTechnicalArtifacts(tryRepairUtf8Mojibake(next));
      if (decoded && decoded !== next) {
        next = decoded;
        changed = true;
      }
    }

    if (!changed) {
      break;
    }
  }

  const utf8Candidate = normalizeTechnicalArtifacts(tryRepairUtf8Mojibake(next));
  if (utf8Candidate && scoreReadableCyrillic(utf8Candidate) > scoreReadableCyrillic(next)) {
    next = utf8Candidate;
  }

  return next;
}

export function fixMaybeBrokenText<T>(value: T): T {
  if (typeof value === "string") {
    return fixBrokenCyrillic(value) as T;
  }

  if (Array.isArray(value)) {
    return value.map((item) => fixMaybeBrokenText(item)) as T;
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, fixMaybeBrokenText(item)])
    ) as T;
  }

  return value;
}
